'use client';

import {
  Activity,
  Ban,
  CircleCheck,
  Gavel,
  Shield,
  TrendingDown,
  TriangleAlert,
} from 'lucide-react';

type Variant =
  | 'triangle_alert'
  | 'ban'
  | 'circle_check'
  | 'activity'
  | 'shield'
  | 'gavel';

export interface BigNumberProps {
  variant: Variant;
  percentual: number;
  subtitle: string;
  title: string;
  value: string;
}

const TitleIcon = ({ variant }: { variant: Variant }) => {
  switch (variant) {
    case 'triangle_alert':
      return (
        <div className="p-1 sm:p-1.5 lg:p-2 rounded-lg flex-shrink-0 bg-red-500">
          <TriangleAlert className="h-2 w-2 sm:h-3 sm:w-3 lg:h-4 lg:w-4 text-white" />
        </div>
      );
    case 'ban':
      return (
        <div className="p-1 sm:p-1.5 lg:p-2 rounded-lg flex-shrink-0 bg-red-600">
          <Ban className=" h-2 w-2 sm:h-3 sm:w-3 lg:h-4 lg:w-4 text-white" />
        </div>
      );
    case 'circle_check':
      return (
        <div className="p-1 sm:p-1.5 lg:p-2 rounded-lg flex-shrink-0 bg-green-500">
          <CircleCheck className="h-2 w-2 sm:h-3 sm:w-3 lg:h-4 lg:w-4 text-white" />
        </div>
      );
    case 'activity':
      return (
        <div className="p-1 sm:p-1.5 lg:p-2 rounded-lg flex-shrink-0 bg-orange-500">
          <Activity className=" h-2 w-2 sm:h-3 sm:w-3 lg:h-4 lg:w-4 text-white" />
        </div>
      );
    case 'shield':
      return (
        <div className="p-1 sm:p-1.5 lg:p-2 rounded-lg flex-shrink-0 bg-yellow-600">
          <Shield className="h-2 w-2 sm:h-3 sm:w-3 lg:h-4 lg:w-4 text-white" />
        </div>
      );
    default:
      return (
        <div className="p-1 sm:p-1.5 lg:p-2 rounded-lg flex-shrink-0 bg-purple-600">
          <Gavel className="h-2 w-2 sm:h-3 sm:w-3 lg:h-4 lg:w-4 text-white" />
        </div>
      );
  }
};

const BigNumber = ({
  variant,
  percentual,
  subtitle,
  title,
  value,
}: BigNumberProps) => {
  const goodData =
    variant === 'activity' || variant === 'gavel' || variant === 'shield';
  const isPositive = goodData ? percentual < 0 : percentual >= 0;

  return (
    <div
      data-slot="card"
      className="flex-1 bg-card text-card-foreground flex flex-col gap-6 rounded-xl border border-border relative"
    >
      <div
        data-slot="card-content"
        className="[&amp;:last-child]:pb-6 p-3 sm:p-4 lg:p-6"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 sm:gap-2 mb-2">
              <TitleIcon variant={variant} />
              <h3 className="text-xs sm:text-xs lg:text-sm font-medium text-muted-foreground truncate">
                {title}
              </h3>
            </div>
            <div className="mb-2">
              <div className="text-sm sm:text-lg lg:text-2xl font-bold truncate">
                {value}
              </div>
              <p className="text-xs lg:text-sm text-muted-foreground mt-1 line-clamp-2 hidden sm:block">
                {subtitle}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <TrendingDown
                className={`h-2 w-2 sm:h-3 sm:w-3 flex-shrink-0 ${
                  isPositive ? 'text-red-600' : 'text-green-600'
                }`}
              />
              <span
                className={`text-xs lg:text-sm font-medium truncate ${
                  isPositive ? 'text-red-600' : 'text-green-600'
                }`}
              >
                {percentual > 0 ? '+' : '-'}
                {Math.abs(percentual)}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BigNumber;
